// MACHINERY V2 (Stroje) — the katalog filter sheet's EIGHT sections (M2.16c).
//
// Split from CatalogFilterSheet.tsx so the sheet stays composition + chrome and
// this file is the content — the same seam fleet/fleetSheetSections.tsx took
// from fleet/FleetFilterSheet.tsx.
//
// ============================================================
//  WHY EIGHT, WHEN THE BOARD DRAWS THE KATALOG SHEET NOT AT ALL
// ============================================================
// 1 VYROBCA · 2 MIDAS READINESS · 3 OTACKY VRETENA · 4 VYKON VRETENA ·
// 5 POCET NASTROJOV · 6 KUZEL · 7 RIADIACI SYSTEM · 8 TYP OPERACIE.
//
// That is CatalogFilterRail's section list, one for one. The mobile package
// draws a filter sheet only for the fleet (02-filter-sheet-open.png) and tells
// the katalog to use "the same pattern", so the CONTENT is derived from the rail
// the sheet replaces rather than from a board. THE RULING THIS FOLLOWS is
// fleetSheetSections.tsx:17-19: the mobile pass never removes a working control,
// it re-homes it. At 768-1279 every one of these eight works TODAY — the katalog
// rail has no breakpoint branch before this sub-phase — so shipping fewer would
// be a regression rather than a port.
//
// EVERY SECTION IS STAGED. There are no live-applying mode controls here, so
// none of the eyebrows carries the fleet's "applies immediately" note: on this
// sheet that note would be false eight times. See useCatalogFilterDraft.ts.
//
// >>> EACH RANGE ROW DECLARES ITS OWN NUMERIC DOMAIN, BESIDE ITS PARSER. <<<
// `allowDecimal` sits next to the `parseRangeInt` / `parseRangeFloat` it must
// agree with, so the two cannot drift: the box refuses exactly the characters
// its parser would silently drop. Review finding F3 is what a mismatch looks
// like — "1.5" on screen over a committed 1.
//
// COUNTS ARE PRINTED ONLY WHERE THE API RETURNS ONE. The brand facets carry
// `machineCount`; taper, control system and operation type do NOT — the endpoint
// returns no counts for them and inventing one client-side would be a number we
// cannot stand behind. That is the rail's own rule (CatalogFilterRail.tsx:38-42)
// and `SheetCheckRow`'s `count` is optional precisely so a surface can decline
// to print one.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { RD } from "../readiness/readinessPresentation";
import { SheetCheckRow, SheetSection } from "../mobile/sheetAtoms";
import { SheetRangeRow } from "../mobile/SheetRangeRow";
import CatalogSheetBrands from "./catalogSheetBrands";
import { CatalogFacetModel } from "./catalogFacets";
import { parseRangeFloat, parseRangeInt } from "./catalogQuery";
import { CatalogFilterDraftApi } from "./useCatalogFilterDraft";
import useCatalogBrowse from "./useCatalogBrowse";

/** The pinned readiness signal dot, exactly as the rail's RailToggleRow paints
 *  it: cross-palette brand marks from `RD`, never palette tokens. */
const SignalDot: React.FC<{ tone: "oem" | "midas" }> = ({ tone }) => (
  <span
    aria-hidden
    style={{
      width: 8,
      height: 8,
      borderRadius: 999,
      background: tone === "oem" ? RD.oem : RD.midas,
      display: "inline-block",
    }}
  />
);

export interface CatalogSheetSectionsProps {
  api: CatalogFilterDraftApi;
  c: ReturnType<typeof useCatalogBrowse>;
  facets: CatalogFacetModel;
}

const CatalogSheetSections: React.FC<CatalogSheetSectionsProps> = ({ api, c, facets }) => {
  const { translate } = useTranslation("myMachinery");
  const { draft, patch } = api;

  return (
    <>
      <CatalogSheetBrands
        facets={facets}
        brandSearch={c.brandSearch}
        onBrandSearchChange={c.setBrandSearch}
        totalCount={c.browseTotalCount}
        selectedManufacturerId={draft.manufacturerId}
        onSelect={(manufacturerId) => patch({ manufacturerId })}
        expanded={api.brandsExpanded}
        onExpandedChange={api.setBrandsExpanded}
      />

      <SheetSection title={translate("myMachinery:midasReadiness")}>
        <SheetCheckRow
          label={translate("myMachinery:oemReady")}
          icon={<SignalDot tone="oem" />}
          checked={!!draft.oemReady}
          onToggle={() => api.toggleFlag("oemReady")}
        />
        <SheetCheckRow
          label={translate("myMachinery:machineIdIntegrated")}
          icon={<SignalDot tone="midas" />}
          checked={!!draft.machineIdIntegrated}
          onToggle={() => api.toggleFlag("machineIdIntegrated")}
        />
      </SheetSection>

      <SheetSection title={translate("myMachinery:spindleRpmRange")}>
        <SheetRangeRow
          title={translate("myMachinery:spindleRpmRange")}
          min={draft.minRpm}
          max={draft.maxRpm}
          // `parseRangeInt` — so the box must refuse a decimal point, or it
          // would show "1.5" over a committed 1 (review finding F3).
          allowDecimal={false}
          onMin={(raw) => patch({ minRpm: parseRangeInt(raw) })}
          onMax={(raw) => patch({ maxRpm: parseRangeInt(raw) })}
        />
      </SheetSection>

      {/* HP, not kW — the rail resolved that artboard-vs-data-package conflict
          FOR the data package (CatalogFilterRail.tsx:167-171), because
          relabelling the same unfiltered number as kW would send a value the API
          reads as horsepower. The sheet must not resolve it the other way. */}
      <SheetSection title={translate("myMachinery:spindleHpRange")}>
        <SheetRangeRow
          title={translate("myMachinery:spindleHpRange")}
          min={draft.minHp}
          max={draft.maxHp}
          // The ONLY decimal field of the three — `parseRangeFloat`.
          allowDecimal
          onMin={(raw) => patch({ minHp: parseRangeFloat(raw) })}
          onMax={(raw) => patch({ maxHp: parseRangeFloat(raw) })}
        />
      </SheetSection>

      <SheetSection title={translate("myMachinery:v2.catalog.toolCountRange")}>
        <SheetRangeRow
          title={translate("myMachinery:v2.catalog.toolCountRange")}
          min={draft.minTools}
          max={draft.maxTools}
          // A tool count is an integer — `parseRangeInt`.
          allowDecimal={false}
          onMin={(raw) => patch({ minTools: parseRangeInt(raw) })}
          onMax={(raw) => patch({ maxTools: parseRangeInt(raw) })}
        />
      </SheetSection>

      {c.taperTypes.length > 0 && (
        <SheetSection title={translate("myMachinery:taperType")}>
          <SheetCheckRow
            label={translate("myMachinery:allTaperTypes")}
            checked={!draft.taperTypeId}
            onToggle={() => patch({ taperTypeId: undefined })}
          />
          {c.taperTypes.map((tt) => (
            <SheetCheckRow
              key={tt.id}
              label={tt.name}
              checked={draft.taperTypeId === tt.id}
              onToggle={() =>
                patch({ taperTypeId: draft.taperTypeId === tt.id ? undefined : tt.id })
              }
            />
          ))}
        </SheetSection>
      )}

      {c.controlSystemBrands.length > 0 && (
        <SheetSection title={translate("myMachinery:controlSystemBrand")}>
          <SheetCheckRow
            label={translate("myMachinery:v2.catalog.allControlBrands")}
            checked={!draft.controlSystemBrandId}
            onToggle={() => patch({ controlSystemBrandId: undefined })}
          />
          {c.controlSystemBrands.map((b) => (
            <SheetCheckRow
              key={b.id}
              label={b.name}
              checked={draft.controlSystemBrandId === b.id}
              onToggle={() =>
                patch({
                  controlSystemBrandId:
                    draft.controlSystemBrandId === b.id ? undefined : b.id,
                })
              }
            />
          ))}
        </SheetSection>
      )}

      {c.operationTypes.length > 0 && (
        <SheetSection title={translate("myMachinery:operationType")}>
          <SheetCheckRow
            label={translate("myMachinery:v2.catalog.allOperationTypes")}
            checked={!draft.operationTypeId}
            onToggle={() => patch({ operationTypeId: undefined })}
          />
          {c.operationTypes.map((o) => (
            <SheetCheckRow
              key={o.id}
              label={o.name}
              checked={draft.operationTypeId === o.id}
              onToggle={() =>
                patch({
                  operationTypeId: draft.operationTypeId === o.id ? undefined : o.id,
                })
              }
            />
          ))}
        </SheetSection>
      )}
    </>
  );
};

export default CatalogSheetSections;
